import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Logo from "@/components/Logo";
import AnimatedPortrait from "@/components/AnimatedPortrait";
import {
  ArrowRight, ArrowLeft, Mic, MicOff, Send, Volume2, VolumeX, X, Play,
  Sparkles, Loader2, ChevronRight, Trophy, BookOpen, AlertTriangle, Lightbulb,
} from "lucide-react";

/* ---------- Avatar gallery ---------- */
export function SimulatorGallery() {
  const nav = useNavigate();
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    api.get("/simulator/avatars")
      .then(({ data }) => setAvatars(data))
      .catch(() => toast.error("Failed to load avatars"))
      .finally(() => setLoading(false));
  }, []);

  const start = async (av) => {
    setStarting(av.id);
    try {
      const { data } = await api.post("/simulator/sessions", { avatar_id: av.id });
      nav(`/learn/simulator/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start session");
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/learn" className="text-sm text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1.5">
            <ArrowLeft size={14}/> Back to learning
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-2">Live sales practice</div>
          <h1 className="font-display text-4xl md:text-5xl text-[#222222] mb-3">Sales Simulator</h1>
          <p className="text-slate-600 max-w-2xl">
            Pick a CXO. Have a real conversation. The coach panel guides you in real-time using
            <strong> Module 3</strong> (Application Guide) and <strong>Module 4.3</strong> (Objection Handling).
            You can type or speak.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading personas…</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="simulator-avatar-grid">
            {avatars.map((av) => (
              <Card
                key={av.id}
                data-testid={`avatar-${av.id}`}
                className="group overflow-hidden border-slate-200 hover:border-[#E53935]/40 hover:shadow-xl transition-all"
              >
                <div className="relative h-48 bg-[#222222] overflow-hidden flex items-center justify-center">
                  <AnimatedPortrait
                    src={av.photo_url}
                    alt={av.name}
                    size={160}
                    rounded="rounded-full"
                    className="!shadow-none"
                  />
                  <div className="absolute top-3 right-3 text-2xl" title={av.region}>{av.country_flag}</div>
                </div>
                <div className="p-5">
                  <div className="text-slate-900 font-display font-semibold text-lg leading-tight mb-0.5">{av.name}</div>
                  <div className="text-[11px] text-slate-500 mb-3">{av.role}</div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E53935]/10 text-[#E53935] font-bold">{av.industry}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{av.region}</span>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-3 min-h-[3.6em] mb-4">{av.context_brief}</p>
                  <Button
                    data-testid={`start-${av.id}`}
                    onClick={() => start(av)}
                    disabled={starting === av.id}
                    className="w-full bg-[#222222] hover:bg-black text-white gap-2 group/btn"
                  >
                    {starting === av.id ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
                    Start practice
                    <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform"/>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ---------- Live chat ---------- */
export function SimulatorChat() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [ttsOn, setTtsOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [ending, setEnding] = useState(false);
  const [scorecard, setScorecard] = useState(null);

  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const threadRef = useRef(null);
  const audioCtxRef = useRef(null);
  const recStartRef = useRef(0);
  const speechRecRef = useRef(null);              // Web Speech API instance
  const speechFinalRef = useRef("");              // finalized chunks for current rec
  const [recSeconds, setRecSeconds] = useState(0);
  const [recLevel, setRecLevel] = useState(0); // 0..1
  const [interimText, setInterimText] = useState(""); // live, not yet finalized
  // Browser support for live STT (Chrome/Edge only — Safari/Firefox fall back to Whisper)
  const supportsSpeechRec = typeof window !== "undefined"
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const messages = session?.messages || [];
  const coachLatest = (session?.coach_notes || [])[((session?.coach_notes || []).length || 0) - 1];

  useEffect(() => {
    api.get(`/simulator/sessions/${sessionId}`)
      .then(({ data }) => {
        setSession(data);
        if (data.scorecard) setScorecard(data.scorecard);
      })
      .catch(() => toast.error("Session not found"))
      .finally(() => setLoading(false));
  }, [sessionId]);

  // The opening coach panel is seeded instantly from a static cheat sheet so
  // "Start practice" returns in <200ms. The Claude-generated coach note lands
  // in Mongo a few seconds later — poll up to 6× (~36s) until it arrives, then
  // swap it in without disturbing the rest of the UI.
  useEffect(() => {
    if (!session) return undefined;
    const notes = session.coach_notes || [];
    const first = notes[0];
    if (!first || !first.pending_llm) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/simulator/sessions/${sessionId}`);
        if (cancelled) return;
        const upgraded = (data.coach_notes || [])[0];
        if (upgraded && !upgraded.pending_llm) {
          setSession(data);
          return; // done
        }
      } catch (_) { /* ignore */ }
      if (!cancelled && attempts < 6) setTimeout(tick, 6000);
    };
    const id = setTimeout(tick, 4000);
    return () => { cancelled = true; clearTimeout(id); };
  }, [sessionId, session?.id]);

  // Auto-scroll to latest message
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Auto-play TTS for any customer message that hasn't been heard yet.
  // We keep a per-message blob URL cache so the manual "Play" button on a
  // bubble can replay it instantly without hitting the API again.
  const [audioByIdx, setAudioByIdx] = useState({}); // idx -> blob url
  const [audioPlayingIdx, setAudioPlayingIdx] = useState(null);
  // Real-time amplitude (0..1) read off the playing TTS audio so we can sync
  // the avatar's "mouth pulse" to the actual voice rhythm instead of a canned loop.
  const [speakAmp, setSpeakAmp] = useState(0);
  const playAnalyserRef = useRef(null);
  const playAudioCtxRef = useRef(null);
  const playRafRef = useRef(null);
  // Tracks the last index for which we kicked off a TTS fetch so we can show
  // a "preparing voice…" indicator on the bubble *before* audio actually starts.
  const [audioLoadingIdx, setAudioLoadingIdx] = useState(null);
  // For auto-stop-on-silence: timestamp of the last sample above threshold.
  const lastSpeechAtRef = useRef(0);
  const SILENCE_AUTO_STOP_MS = 5000;
  const SILENCE_RMS_THRESHOLD = 0.025;

  const fetchAudio = async (idx, text) => {
    if (audioByIdx[idx]) return audioByIdx[idx];
    setAudioLoadingIdx(idx);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/simulator/sessions/${sessionId}/voice/tts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("uvc_token")}`,
          },
          body: JSON.stringify({ text }),
        },
      );
      if (!res.ok) throw new Error(`TTS HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAudioByIdx((m) => ({ ...m, [idx]: url }));
      return url;
    } finally {
      setAudioLoadingIdx((cur) => (cur === idx ? null : cur));
    }
  };

  const playBubble = async (idx, text) => {
    try {
      const url = await fetchAudio(idx, text);
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Tear down any previous analyser loop
      if (playRafRef.current) cancelAnimationFrame(playRafRef.current);
      if (playAudioCtxRef.current) {
        try { playAudioCtxRef.current.close(); } catch (err) { console.debug("audio ctx close (teardown):", err?.message); }
      }
      setSpeakAmp(0);

      const audio = new Audio(url);
      audio.crossOrigin = "anonymous";
      audioRef.current = audio;
      setAudioPlayingIdx(idx);
      audio.onended = () => {
        setAudioPlayingIdx((cur) => (cur === idx ? null : cur));
        setSpeakAmp(0);
        if (playRafRef.current) cancelAnimationFrame(playRafRef.current);
        if (playAudioCtxRef.current) {
          try { playAudioCtxRef.current.close(); } catch (err) { console.debug("audio ctx close (ended):", err?.message); }
          playAudioCtxRef.current = null;
        }
      };

      // Hook an AnalyserNode to the audio element so we can read amplitude
      // frame by frame and drive the avatar's mouth-pulse animation from the
      // actual TTS voice. Same-origin blob URL → no CORS issues.
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        playAudioCtxRef.current = ctx;
        const src = ctx.createMediaElementSource(audio);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        src.connect(analyser);
        // Important: also pipe to destination so we still HEAR the audio.
        analyser.connect(ctx.destination);
        playAnalyserRef.current = analyser;

        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (!playAnalyserRef.current) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i += 1) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / buf.length);
          // Map RMS (~0..0.3 in normal speech) into 0..1 with a soft ceiling
          setSpeakAmp(Math.min(1, rms * 3.5));
          playRafRef.current = requestAnimationFrame(tick);
        };
        playRafRef.current = requestAnimationFrame(tick);
      } catch (analyserErr) {
        // If WebAudio path fails (rare) we fall back to the canned CSS pulse.
        console.warn("[Simulator] analyser hook failed:", analyserErr);
      }

      await audio.play();
    } catch (e) {
      console.error("[Simulator] TTS playback error:", e);
      toast.error("Could not play voice", { description: String(e?.message || e).slice(0, 120) });
      setAudioPlayingIdx(null);
      setSpeakAmp(0);
    }
  };

  // Try to auto-play the latest customer message; if browser blocks autoplay,
  // the per-bubble "Play" button still works (real user click). We defer the
  // call to a microtask so React doesn't see a setState during render.
  useEffect(() => {
    if (!ttsOn || !messages.length) return undefined;
    const lastIdx = messages.length - 1;
    const last = messages[lastIdx];
    if (last?.role !== "customer") return undefined;
    if (audioByIdx[lastIdx]) return undefined;
    const tid = setTimeout(() => {
      playBubble(lastIdx, last.text).catch(() => { /* autoplay blocked */ });
    }, 0);
    return () => clearTimeout(tid);
  }, [messages.length, ttsOn]);

  const sendText = async (override) => {
    const body = (override ?? text).trim();
    if (!body) return;
    setSending(true);
    setText("");
    // Optimistic
    setSession((s) => s ? { ...s, messages: [...(s.messages || []), { role: "salesperson", text: body, ts: new Date().toISOString() }] } : s);
    try {
      const { data } = await api.post(`/simulator/sessions/${sessionId}/message`, { text: body });
      setSession((s) => s ? {
        ...s,
        messages: [...(s.messages || []).filter((m, i, arr) => !(i === arr.length - 1 && m.role === "salesperson" && m.text === body)), data.user_message, data.avatar_message],
        coach_notes: [...(s.coach_notes || []), data.coach],
      } : s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  // ----- Voice recording (tap to start, tap to stop) ----------------------
  // We prefer the browser's Web Speech API for *live* word-by-word streaming
  // (Chrome/Edge). On Safari/Firefox we fall back to recording -> Whisper.
  // Either way the transcript lands in the text input first so the user
  // can review and edit before pressing Send.
  const stopMeter = () => {
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (err) { console.debug("mic meter ctx close:", err?.message); }
      audioCtxRef.current = null;
    }
    setRecLevel(0);
  };

  // Shared audio-level meter for both live and fallback paths.
  // Declared BEFORE startLiveSTT/startWhisperFallback so the lint
  // analyser can statically resolve the reference.
  const setupAudioMeter = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      mediaRecRef.current = mediaRecRef.current || { stream };
      const data = new Uint8Array(analyser.frequencyBinCount);
      lastSpeechAtRef.current = Date.now();
      const tick = () => {
        if (!audioCtxRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        if (rms > SILENCE_RMS_THRESHOLD) lastSpeechAtRef.current = Date.now();
        setRecLevel(Math.min(1, rms * 3));
        setRecSeconds(Math.floor((Date.now() - recStartRef.current) / 1000));
        // Auto-stop after 5s of silence (but require at least 1s of recording
        // first so we don't kill the session before the user even speaks).
        const recElapsed = Date.now() - recStartRef.current;
        if (recElapsed > 1500 && Date.now() - lastSpeechAtRef.current > SILENCE_AUTO_STOP_MS) {
          toast.message("Stopped — no speech detected for 5 seconds.");
          stopRecording();
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      console.warn("[Simulator] meter init failed:", e);
    }
  };

  // ----- LIVE STREAM (Web Speech API) ----------------------
  const startLiveSTT = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return false;
    // Need an audio stream so we can drive the level meter (Web Speech API
    // doesn't expose audio frames directly).
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.error("[Simulator] mic permission error:", e);
      toast.error("Microphone permission denied", { description: e?.message });
      return true; // we handled the failure
    }
    const sr = new SR();
    sr.continuous = true;
    sr.interimResults = true;
    sr.lang = navigator.language || "en-US";
    speechFinalRef.current = "";
    setInterimText("");

    sr.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const r = ev.results[i];
        const t = r[0].transcript;
        if (r.isFinal) {
          speechFinalRef.current = (speechFinalRef.current + " " + t).trim();
        } else {
          interim += t;
        }
      }
      setInterimText(interim);
      // Live-update the text input as we stream
      const live = (speechFinalRef.current + " " + interim).replace(/\s+/g, " ").trim();
      setText(live);
    };
    sr.onerror = (e) => {
      console.error("[Simulator] SpeechRecognition error:", e?.error || e);
      if (e?.error === "no-speech") return; // benign
      if (e?.error === "not-allowed") {
        toast.error("Microphone permission denied");
        stopRecording();
      }
    };
    sr.onend = () => {
      // If we're still in recording state, the recognizer auto-stopped (some
      // browsers do this after silence) — restart so it keeps streaming.
      if (recording && speechRecRef.current === sr) {
        try { sr.start(); } catch (err) { console.debug("speech recognizer restart:", err?.message); }
      }
    };
    try { sr.start(); } catch (e) {
      console.error("[Simulator] sr.start failed:", e);
      toast.error("Could not start live transcription", { description: e?.message });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    }
    speechRecRef.current = sr;

    // Set up audio level meter from the same stream
    recStartRef.current = Date.now();
    setRecSeconds(0);
    setRecording(true);
    setupAudioMeter(stream);
    return true;
  };

  // ----- WHISPER FALLBACK (record then upload) --------------
  const startWhisperFallback = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
      const mime = candidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stopMeter();
        stream.getTracks().forEach((t) => t.stop());
        const actualMime = mr.mimeType || mime || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: actualMime });
        if (blob.size < 1200) {
          toast.error("Recording too short", { description: "Tap the mic and speak for at least a second before tapping again to stop." });
          return;
        }
        setTranscribing(true);
        try {
          const ext = /mp4/.test(actualMime) ? "m4a" : /ogg/.test(actualMime) ? "ogg" : "webm";
          const fd = new FormData();
          fd.append("file", blob, `voice.${ext}`);
          const { data } = await api.post(`/simulator/sessions/${sessionId}/voice/stt`, fd);
          const transcript = (data?.text || "").trim();
          if (transcript) {
            setText((prev) => (prev ? `${prev} ${transcript}` : transcript));
            toast.success("Got it — review and press Send", { duration: 2500 });
          } else {
            toast.message("Didn't catch any words — try again, closer to the mic.");
          }
        } catch (e) {
          console.error("[Simulator] STT error:", e?.response?.data || e);
          toast.error("Transcription failed", {
            description: e?.response?.data?.detail || e?.message || "Unknown error",
          });
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecRef.current = mr;
      recStartRef.current = Date.now();
      setRecSeconds(0);
      setRecording(true);
      setupAudioMeter(stream);
    } catch (e) {
      console.error("[Simulator] mic permission error:", e);
      toast.error("Microphone permission denied", { description: e?.message });
    }
  };

  const startRecording = async () => {
    if (recording || transcribing) return;
    if (supportsSpeechRec) {
      const ok = await startLiveSTT();
      if (ok) return;
    }
    await startWhisperFallback();
  };

  const stopRecording = () => {
    if (!recording) return;
    setRecording(false);
    // Live path: shut down the SpeechRecognition + the audio stream
    if (speechRecRef.current) {
      try { speechRecRef.current.onend = null; speechRecRef.current.stop(); } catch (err) { console.debug("speech recognizer stop:", err?.message); }
      speechRecRef.current = null;
      const holder = mediaRecRef.current;
      if (holder?.stream && typeof holder.stream.getTracks === "function") {
        holder.stream.getTracks().forEach((t) => t.stop());
      }
      mediaRecRef.current = null;
      stopMeter();
      setInterimText("");
      // Live transcript is already in the text input. Final cleanup:
      const finalTxt = (speechFinalRef.current || "").trim();
      if (finalTxt) {
        toast.success("Got it — review and press Send", { duration: 2200 });
      } else {
        toast.message("Didn't catch any words — try again, closer to the mic.");
      }
      return;
    }
    // Fallback path: stop the MediaRecorder which will trigger onstop -> Whisper
    try { mediaRecRef.current?.stop?.(); } catch (err) { console.debug("media recorder stop:", err?.message); }
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const endSession = async () => {
    setEnding(true);
    try {
      const { data } = await api.post(`/simulator/sessions/${sessionId}/end`);
      setScorecard(data.scorecard);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate scorecard");
    } finally {
      setEnding(false);
    }
  };

  if (loading) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading session…</div>;
  if (!session) return <div className="p-10 text-slate-500">Session not found.</div>;

  const av = session.avatar || {};

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Top bar */}
      <header className="shrink-0 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/learn/simulator")} className="text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1.5 text-sm" data-testid="sim-back">
              <ArrowLeft size={14}/> Avatars
            </button>
            <div className="w-px h-6 bg-slate-200"/>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-900">Call with {av.name} {av.country_flag}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{av.industry} · {av.region}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTtsOn(v => !v)}
              className="h-8 gap-1.5 text-xs"
              data-testid="toggle-tts"
              title={ttsOn ? "Mute customer voice" : "Hear customer voice"}
            >
              {ttsOn ? <Volume2 size={13}/> : <VolumeX size={13}/>}
              {ttsOn ? "Voice on" : "Voice off"}
            </Button>
            <Button
              size="sm"
              onClick={endSession}
              disabled={ending || scorecard}
              className="h-8 gap-1.5 text-xs bg-[#E53935] hover:bg-[#c62828] text-white"
              data-testid="end-session"
            >
              {ending ? <Loader2 size={12} className="animate-spin"/> : <Trophy size={12}/>}
              End & get score
            </Button>
          </div>
        </div>
      </header>

      {/* Split pane */}
      <div className="flex-1 min-h-0 grid lg:grid-cols-5">
        {/* Conversation pane */}
        <main className="lg:col-span-3 flex flex-col min-h-0 border-r border-slate-200 bg-white">
          {/* Big Zoom-style "video call" tile — current speaker prominently shown */}
          <div className="shrink-0 px-6 pt-5 pb-3 bg-gradient-to-b from-[#222222] to-[#1a1a1a] relative overflow-hidden" data-testid="call-tile">
            <div className="absolute inset-0 acoustic-rings opacity-10 pointer-events-none"/>
            <div className="relative flex items-center justify-center">
              <div className="flex flex-col items-center gap-2.5">
                <AnimatedPortrait
                  src={av.photo_url}
                  alt={av.name}
                  size={180}
                  rounded="rounded-2xl"
                  state={audioPlayingIdx != null ? "speaking" : recording ? "listening" : "idle"}
                  amp={speakAmp}
                  className="!shadow-2xl"
                />
                <div className="text-center">
                  <div className="text-white font-display font-semibold text-base leading-tight">
                    {av.name} <span className="ml-0.5">{av.country_flag}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[2px] text-white/60 mt-0.5">{av.role}</div>
                  <div className="text-[10px] uppercase tracking-[2px] mt-1 inline-flex items-center gap-1.5">
                    {audioPlayingIdx != null && (
                      <><span className="w-1.5 h-1.5 rounded-full bg-[#E53935] animate-pulse"/><span className="text-[#E53935] font-bold">Speaking…</span></>
                    )}
                    {audioPlayingIdx == null && recording && (
                      <><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/><span className="text-emerald-300 font-bold">Listening to you</span></>
                    )}
                    {audioPlayingIdx == null && !recording && (
                      <><span className="w-1.5 h-1.5 rounded-full bg-white/30"/><span className="text-white/50">On the call</span></>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={threadRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-slate-50/50" data-testid="sim-thread">
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                msg={m}
                avatar={av}
                idx={i}
                onPlay={(idx) => playBubble(idx, m.text)}
                playing={audioPlayingIdx === i}
                loading={audioLoadingIdx === i}
                cached={!!audioByIdx[i]}
              />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-slate-400 italic ml-12">
                <Loader2 size={12} className="animate-spin"/> {av.name} is replying…
              </div>
            )}
          </div>
          {/* Composer */}
          <div className="shrink-0 border-t border-slate-200 px-5 py-3 bg-slate-50/60">
            {/* Live recording indicator with timer + audio level + live transcript hint */}
            {recording && (
              <div className="mb-2 bg-[#E53935]/5 border border-[#E53935]/20 rounded-md px-3 py-2" data-testid="rec-indicator">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#E53935] animate-pulse"/>
                  <span className="text-xs font-medium text-[#E53935] uppercase tracking-wider">
                    {supportsSpeechRec ? "Live · " : "Recording · "}{String(recSeconds).padStart(2, "0")}s
                  </span>
                  <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#E53935] transition-all"
                      style={{ width: `${Math.round(recLevel * 100)}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-slate-500">Auto-stop after 5s silence</span>
                </div>
                {supportsSpeechRec && interimText && (
                  <div className="mt-1.5 text-[11px] italic text-slate-500 truncate" data-testid="interim-text">
                    …{interimText}
                  </div>
                )}
              </div>
            )}
            {transcribing && (
              <div className="text-xs text-slate-500 mb-2 inline-flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin"/> Transcribing your voice…
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                placeholder={recording ? (supportsSpeechRec ? "Listening live… speak now (transcript appears here in real time)" : "Listening… speak now.") : "Your pitch… (Enter to send, Shift+Enter for newline)"}
                rows={2}
                disabled={sending || transcribing || !!scorecard}
                className="flex-1 resize-none bg-white"
                data-testid="sim-input"
              />
              <button
                type="button"
                onClick={toggleRecording}
                disabled={sending || transcribing || !!scorecard}
                className={`shrink-0 w-10 h-10 rounded-full inline-flex items-center justify-center transition-all ${recording ? "bg-[#E53935] text-white scale-110 shadow-lg ring-4 ring-[#E53935]/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                title={recording ? "Tap to stop & transcribe" : "Tap to record your voice"}
                data-testid="sim-mic"
              >
                {recording ? <MicOff size={16}/> : <Mic size={16}/>}
              </button>
              <Button
                onClick={() => sendText()}
                disabled={sending || !text.trim() || !!scorecard}
                className="h-10 bg-[#222222] hover:bg-black text-white gap-1.5"
                data-testid="sim-send"
              >
                {sending ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>} Send
              </Button>
            </div>
          </div>
        </main>

        {/* Coach panel */}
        <aside className="lg:col-span-2 overflow-y-auto px-6 py-6 bg-slate-50">
          <div className="text-[10px] uppercase tracking-[3px] text-[#E53935] font-bold mb-3 inline-flex items-center gap-1.5">
            <Lightbulb size={11}/> Coach · Teleprompter
          </div>
          {!coachLatest ? (
            <Card className="p-5 bg-white">
              <div className="text-sm text-slate-600">
                Take your first turn. The customer just opened — listen, acknowledge, then ask an exploratory question.
              </div>
              <div className="mt-3 text-[11px] text-slate-500 italic">
                Tip: Use the LAER framework — Listen, Acknowledge, Explore, Respond.
              </div>
            </Card>
          ) : (
            <CoachPanel coach={coachLatest} onUseReply={(t) => setText(t)} />
          )}
        </aside>
      </div>

      {/* Scorecard modal */}
      {scorecard && (
        <ScorecardModal scorecard={scorecard} avatar={av} onClose={() => nav("/learn/simulator")} />
      )}
    </div>
  );
}

function MessageBubble({ msg, avatar, idx, onPlay, playing, loading, cached }) {
  const isCustomer = msg.role === "customer";
  if (isCustomer) {
    return (
      <div className="flex items-start gap-2.5" data-testid="bubble-customer">
        <AnimatedPortrait
          src={avatar.photo_url}
          alt={avatar.name}
          size={28}
          state={playing ? "speaking" : "idle"}
          className="shrink-0 mt-0.5"
        />
        <div className="max-w-[80%]">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{avatar.name}</div>
            <button
              type="button"
              onClick={() => onPlay?.(idx)}
              disabled={loading}
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 transition-all ${playing ? "bg-[#E53935] text-white" : loading ? "bg-slate-200 text-slate-500" : "text-slate-500 hover:text-[#E53935] hover:bg-slate-200/60"}`}
              title={playing ? "Playing…" : loading ? "Preparing voice…" : (cached ? "Replay voice" : "Hear voice")}
              data-testid={`bubble-play-${idx}`}
            >
              {playing ? <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>
                : loading ? <Loader2 size={9} className="animate-spin"/>
                : <Play size={9} className="ml-px"/>}
              {playing ? "speaking" : loading ? "preparing" : (cached ? "replay" : "hear")}
            </button>
          </div>
          <div className={`bg-white border border-slate-200 text-slate-900 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed transition-shadow ${playing ? "shadow-[0_0_0_2px_rgba(229,57,53,0.25)]" : ""}`}>{msg.text}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start justify-end gap-3" data-testid="bubble-salesperson">
      <div className="max-w-[80%]">
        <div className="text-[10px] uppercase tracking-wider text-[#E53935] font-semibold mb-1 text-right">You</div>
        <div className="bg-[#222222] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm leading-relaxed">{msg.text}</div>
      </div>
    </div>
  );
}

function CoachPanel({ coach, onUseReply }) {
  const reply = coach?.suggested_reply || "";
  const readyAnswers = Array.isArray(coach?.ready_answers) ? coach.ready_answers : [];
  return (
    <div className="space-y-3" data-testid="coach-panel">
      {readyAnswers.length > 0 && (
        <Card className="p-4 bg-white border-l-4 border-emerald-500">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-2.5 inline-flex items-center gap-1.5">
            <Sparkles size={11}/> Ready-to-send answers
            <span className="text-slate-400 normal-case tracking-normal text-[10px]">· tap to use</span>
          </div>
          <div className="space-y-2">
            {readyAnswers.map((opt, i) => (
              <button
                key={i}
                onClick={() => onUseReply(typeof opt === "string" ? opt : opt?.text || "")}
                className="block w-full text-left text-sm text-slate-700 bg-slate-50 hover:bg-emerald-50 hover:border-emerald-300 border border-slate-200 rounded-md px-3 py-2.5 leading-relaxed transition-colors group"
                data-testid={`ready-answer-${i}`}
              >
                <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold mr-1.5">{String.fromCharCode(65 + i)}</span>
                {typeof opt === "string" ? opt : opt?.text || ""}
                <span className="block text-[10px] text-slate-400 italic mt-1 group-hover:text-emerald-600">↳ tap to drop into input</span>
              </button>
            ))}
          </div>
        </Card>
      )}
      {reply && (
        <Card className="p-4 bg-white border-l-4 border-[#E53935]">
          <div className="text-[10px] uppercase tracking-wider text-[#E53935] font-bold mb-1.5">Suggested reply</div>
          <div className="text-sm text-slate-900 leading-relaxed mb-3">{reply}</div>
          <button
            onClick={() => onUseReply(reply)}
            className="text-[11px] text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1"
            data-testid="use-suggested"
          >
            Use this <ChevronRight size={11}/>
          </button>
        </Card>
      )}
      {coach.talking_points?.length > 0 && (
        <Card className="p-4 bg-white">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 inline-flex items-center gap-1.5">
            <Lightbulb size={11} className="text-amber-500"/> Talking points
          </div>
          <ul className="space-y-2">
            {coach.talking_points.map((tp, i) => (
              <li key={i} className="text-sm text-slate-700 leading-relaxed">
                <span className="text-emerald-600 mr-1.5">✓</span>{tp.point}
                {tp.module_ref && <span className="block text-[11px] text-slate-500 italic mt-0.5 ml-5">→ {tp.module_ref}</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {coach.watch_outs?.length > 0 && (
        <Card className="p-4 bg-amber-50/60 border-amber-200">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-2 inline-flex items-center gap-1.5">
            <AlertTriangle size={11}/> Watch outs
          </div>
          <ul className="space-y-1.5">
            {coach.watch_outs.map((w, i) => (
              <li key={i} className="text-sm text-slate-700 leading-relaxed"><span className="text-amber-600 mr-1.5">⚠</span>{w}</li>
            ))}
          </ul>
        </Card>
      )}
      {coach.references?.length > 0 && (
        <Card className="p-4 bg-white">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 inline-flex items-center gap-1.5">
            <BookOpen size={11}/> References
          </div>
          <ul className="space-y-1.5">
            {coach.references.map((r, i) => (
              <li key={i} className="text-[12px] text-slate-600 leading-relaxed">
                <span className="font-medium text-slate-800">{r.label}</span>
                {r.module && <span className="text-slate-500 italic"> — {r.module}</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function ScorecardModal({ scorecard, avatar, onClose }) {
  const overall = scorecard.overall_score ?? 0;
  const sub = scorecard.subscores || {};
  const tone = overall >= 80 ? "emerald" : overall >= 60 ? "amber" : "red";
  const toneClasses = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  }[tone];
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4 overflow-y-auto py-10" data-testid="scorecard-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-7 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-[#E53935] font-bold mb-1">Session complete</div>
            <h3 className="font-display text-2xl text-slate-900">Practice with {avatar.name}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-scorecard"><X size={18}/></button>
        </div>

        <div className={`rounded-lg border ${toneClasses} px-5 py-4 mb-5 flex items-center justify-between`}>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold mb-0.5">Overall score</div>
            <div className="text-4xl font-display font-bold">{overall}<span className="text-base opacity-60">/100</span></div>
          </div>
          <Trophy size={36} className="opacity-70"/>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          {[
            ["Rapport", sub.rapport],
            ["Accuracy", sub.accuracy],
            ["Objection", sub.objection_handling],
            ["Refs", sub.module_references],
            ["Closing", sub.closing],
          ].map(([label, val]) => (
            <div key={label} className="bg-slate-50 rounded-md px-3 py-2 text-center">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
              <div className="text-xl font-display font-bold text-slate-900 mt-0.5">{val ?? "–"}</div>
            </div>
          ))}
        </div>

        {scorecard.strengths?.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1.5">Strengths</div>
            <ul className="space-y-1">
              {scorecard.strengths.map((s, i) => <li key={i} className="text-sm text-slate-700"><span className="text-emerald-600 mr-1.5">✓</span>{s}</li>)}
            </ul>
          </div>
        )}
        {scorecard.gaps?.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1.5">Gaps</div>
            <ul className="space-y-1">
              {scorecard.gaps.map((s, i) => <li key={i} className="text-sm text-slate-700"><span className="text-amber-600 mr-1.5">⚠</span>{s}</li>)}
            </ul>
          </div>
        )}
        {scorecard.missed_opportunities?.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1.5">Missed opportunities</div>
            <ul className="space-y-1">
              {scorecard.missed_opportunities.map((s, i) => <li key={i} className="text-sm text-slate-700"><span className="text-slate-400 mr-1.5">○</span>{s}</li>)}
            </ul>
          </div>
        )}
        {scorecard.next_practice && (
          <div className="mt-5 rounded-md bg-[#222222] text-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-[#E53935] font-bold mb-0.5">Next practice</div>
            <div className="text-sm">{scorecard.next_practice}</div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="scorecard-close-btn">Close</Button>
          <Button onClick={() => { onClose(); }} className="bg-[#E53935] hover:bg-[#c62828] text-white" data-testid="scorecard-practice-again">
            Try another avatar <ArrowRight size={14} className="ml-1.5"/>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SimulatorGallery;
